import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, FolderPlus, Save } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function CreateModule({ user }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/modules`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, description })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Module created successfully!');
        navigate('/modules');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create module');
      }
    } catch (error) {
      toast.error('Failed to create module');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'teacher') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <p className="text-[#588157] mb-4">Only teachers can create modules</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/modules')}
            data-testid="back-to-modules-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Modules
          </Button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto p-6 md:p-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FolderPlus className="w-10 h-10 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-4xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              Create New Module
            </h1>
          </div>
          <p className="text-lg text-[#588157]">Organize your courses into modules for better structure</p>
        </div>

        <Card className="p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-[#344E41]">Module Name *</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g., Biblical Foundations, New Testament Studies"
                data-testid="module-name-input"
                className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-[#344E41]">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={4}
                placeholder="Describe what students will learn in this module..."
                data-testid="module-description-input"
                className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
              />
            </div>

            <div className="bg-[#E9EDC9] p-4 rounded-lg">
              <p className="text-sm text-[#344E41]">
                <strong>💡 Tip:</strong> Modules help organize related courses together. 
                Students will see their progress across all courses in a module and earn certificates 
                when they complete all courses in a module.
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/modules')}
                data-testid="cancel-button"
                className="flex-1 border-2 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white duration-300 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="create-module-submit-button"
                className="flex-1 bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg py-6 text-lg"
              >
                <Save className="w-5 h-5 mr-2" strokeWidth={1.5} />
                {loading ? 'Creating...' : 'Create Module'}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
